import { authLocalStorage } from "./authStorage";

// The signed-in user's profile, as returned by /handleUserData. Persisted in
// localStorage so it survives reloads and is shared across tabs; read it only
// through this module.

const STRING_KEYS = {
  spinId: "spin_id",
  dealerCode: "dealer_code",
  regionCode: "region_code",
  jobCode: "job_code",
  nameId: "name_id",
  legacyProfileId: "legacyProfileID",
  firstName: "firstName",
  lastName: "lastName",
  email: "email",
  personType: "personType",
  userId: "user_id",
  pictureUrl: "pictureUrl",
};

const BOOLEAN_KEYS = {
  isAdmin: "isAdmin",
  isGuest: "isGuest",
  isCorporate: "isCorporate",
  disableCommenting: "disableCommenting",
};

const PROFILE_KEYS = new Set([
  ...Object.values(STRING_KEYS),
  ...Object.values(BOOLEAN_KEYS),
]);

const CHANGE_EVENT = "engage:current-user-changed";

// Absent text reads as ""; earlier writes also stored "undefined"/"null".
const readString = (key) => {
  const value = authLocalStorage.get(key);
  return value && value !== "undefined" && value !== "null" ? value : "";
};

// The API sends flags as true or "True"; both end up as text in storage.
const isTrue = (value) => String(value).toLowerCase() === "true";

const readBoolean = (key) => isTrue(authLocalStorage.get(key));

export const getCurrentUser = () => {
  const user = {};
  for (const [field, key] of Object.entries(STRING_KEYS)) {
    user[field] = readString(key);
  }
  for (const [field, key] of Object.entries(BOOLEAN_KEYS)) {
    user[field] = readBoolean(key);
  }
  return user;
};

export const notifyCurrentUserChanged = () => {
  globalThis.dispatchEvent(new Event(CHANGE_EVENT));
};

const writeString = (key, value) =>
  authLocalStorage.set(key, value == null ? "" : String(value));

export const setCurrentUser = (user) => {
  // Guests have no dealership; 90010 is the corporate code the UI has always shown them.
  writeString(STRING_KEYS.dealerCode, user.dealerCode ?? "90010");
  writeString(STRING_KEYS.spinId, user.spinId);
  writeString(STRING_KEYS.regionCode, user.regionCode);
  writeString(STRING_KEYS.jobCode, user.primaryJobCode);
  writeString(STRING_KEYS.nameId, user.nameID);
  writeString(STRING_KEYS.legacyProfileId, user.legacyProfileID);
  writeString(STRING_KEYS.firstName, user.firstName);
  writeString(STRING_KEYS.lastName, user.lastName);
  writeString(STRING_KEYS.email, user.email);
  writeString(STRING_KEYS.personType, user.TMSPersonType);
  writeString(STRING_KEYS.userId, user.userRecord?.user_id);
  for (const [field, key] of Object.entries(BOOLEAN_KEYS)) {
    writeString(key, isTrue(user[field]));
  }
  notifyCurrentUserChanged();
};

// "" records that the profile has no picture, so it isn't fetched again.
export const setCurrentUserPictureUrl = (pictureUrl) => {
  writeString(STRING_KEYS.pictureUrl, pictureUrl);
  notifyCurrentUserChanged();
};

// useSyncExternalStore needs a stable snapshot between changes.
let snapshot = null;
let snapshotKey = null;

export const getCurrentUserSnapshot = () => {
  const user = getCurrentUser();
  const key = JSON.stringify(user);
  if (key !== snapshotKey) {
    snapshot = user;
    snapshotKey = key;
  }
  return snapshot;
};

export const subscribeToCurrentUser = (listener) => {
  // key is null when another tab clears storage (logout).
  const onStorage = (event) => {
    if (event.key === null || PROFILE_KEYS.has(event.key)) {
      listener();
    }
  };
  globalThis.addEventListener("storage", onStorage);
  globalThis.addEventListener(CHANGE_EVENT, listener);
  return () => {
    globalThis.removeEventListener("storage", onStorage);
    globalThis.removeEventListener(CHANGE_EVENT, listener);
  };
};
